// src/modules/production/Production.tsx
import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Tooltip,
  DatePicker,
  Row,
  Col,
  Tabs,
  App,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  DeleteOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

import { useManufacturing } from '@/hooks/useManufacturing';
import { ManufacturingLog } from '@/services/api/manufacturing';
import { ManufacturingForm } from './components/ManufacturingForm';
import { ManufacturingStats } from './components/ManufacturingStats';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

dayjs.locale('ru');

export const Production: React.FC = () => {
  const { modal } = App.useApp();
  
  const {
    logs,
    stats,
    pagination,
    loading,
    actionLoading,
    loadLogs,
    loadStats,
    deleteLog,
    refresh,
  } = useManufacturing();

  // Состояние UI
  const [formVisible, setFormVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('logs');
  
  // Фильтры
  const [filters, setFilters] = useState({
    search: '',
    date_from: undefined as string | undefined,
    date_to: undefined as string | undefined,
    ordering: '-timestamp',
  });

  // Применение фильтров
  const applyFilters = () => {
    loadLogs({
      ...filters,
      page: 1,
      limit: pagination.limit,
    });
  };

  // Сброс фильтров
  const resetFilters = () => {
    setFilters({
      search: '',
      date_from: undefined,
      date_to: undefined,
      ordering: '-timestamp',
    });
    loadLogs({ ordering: '-timestamp' });
  };

  // Обработка изменения даты
  const handleDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters(prev => ({
        ...prev,
        date_from: dates[0].format('YYYY-MM-DD'),
        date_to: dates[1].format('YYYY-MM-DD'),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        date_from: undefined,
        date_to: undefined,
      }));
    }
  };

  // Обработка изменения таблицы
  const handleTableChange = (pag: any) => {
    loadLogs({
      ...filters,
      page: pag.current,
      limit: pag.pageSize,
    });
  };

  // Удаление записи
  const handleDelete = (logId: number) => {
    console.log('🗑️ Вызвана функция handleDelete, logId:', logId);
    
    modal.confirm({
      title: 'Удалить запись о производстве?',
      content: 'Количество произведенного будет вычтено из общего количества. Это действие нельзя отменить.',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: async () => {
        console.log('✅ Нажата кнопка OK в модальном окне');
        await deleteLog(logId);
      },
      onCancel: () => {
        console.log('❌ Нажата кнопка Отмена');
      },
    });
  };

  // Колонки таблицы
  const columns: ColumnsType<ManufacturingLog> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true,
    },
    {
      title: 'Блюдо',
      key: 'menu_item',
      width: 250,
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.menu_item.name}</div>
          {record.menu_item.category && record.menu_item.category.length > 0 && (
            <div className="mt-1">
              <Space size={[0, 4]} wrap>
                {record.menu_item.category.map((cat, idx) => (
                  <Tag key={idx} color="blue" className="text-xs">{cat}</Tag>
                ))}
              </Space>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Количество',
      dataIndex: 'quantity_added',
      key: 'quantity_added',
      width: 120,
      sorter: true,
      align: 'center',
      render: (quantity: number) => {
        const isPositive = quantity > 0;
        const isNegative = quantity < 0;
        
        return (
          <Tag 
            color={isPositive ? "success" : isNegative ? "error" : "default"} 
            className="text-base font-bold"
          >
            {isPositive ? `+${quantity}` : quantity} шт.
          </Tag>
        );
      },
    },
    {
      title: 'Дата и время',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      sorter: true,
      render: (timestamp: string) => (
        <div>
          <div>{dayjs(timestamp).format('DD.MM.YYYY')}</div>
          <div className="text-sm text-gray-500">{dayjs(timestamp).format('HH:mm:ss')}</div>
        </div>
      ),
    },
    {
      title: 'Создано',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 150,
      render: (createdBy: string) => createdBy || <Text type="secondary">-</Text>,
    },
    {
      title: 'Комментарий',
      dataIndex: 'comment',
      key: 'comment',
      ellipsis: true,
      render: (comment: string) => comment || <Text type="secondary" italic>Нет комментария</Text>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="Удалить запись">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            loading={actionLoading}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Заголовок */}
      <div className="mb-6">
        <Title level={2} className="mb-1">Производство и склад</Title>
        <Text type="secondary">Управление производством блюд и учет запасов</Text>
      </div>

      {/* Вкладки */}
      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        items={[
          {
            key: 'logs',
            label: '📋 История производства',
          },
          {
            key: 'stats',
            label: '📊 Статистика',
          },
        ]}
      />

      {/* История производства */}
      {activeTab === 'logs' && (
        <>
          {/* Панель действий */}
          <Card className="mb-4">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Input
                  placeholder="Поиск по блюду, пользователю..."
                  prefix={<SearchOutlined />}
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onPressEnter={applyFilters}
                />
              </Col>
              
              <Col xs={24} md={8}>
                <RangePicker
                  style={{ width: '100%' }}
                  placeholder={['Дата от', 'Дата до']}
                  format="DD.MM.YYYY"
                  onChange={handleDateChange}
                />
              </Col>

              <Col xs={24} md={4}>
                <Select
                  style={{ width: '100%' }}
                  value={filters.ordering}
                  onChange={(value) => setFilters(prev => ({ ...prev, ordering: value }))}
                >
                  <Option value="-timestamp">Сначала новые</Option>
                  <Option value="timestamp">Сначала старые</Option>
                  <Option value="-quantity_added">Больше количество</Option>
                  <Option value="quantity_added">Меньше количество</Option>
                </Select>
              </Col>

              <Col xs={24} md={4}>
                <Space>
                  <Button 
                    type="primary" 
                    icon={<FilterOutlined />}
                    onClick={applyFilters}
                    loading={loading}
                  >
                    Применить
                  </Button>
                  <Button onClick={resetFilters}>Сброс</Button>
                </Space>
              </Col>
            </Row>

            <Row gutter={16} className="mt-4">
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setFormVisible(true)}
                  size="large"
                >
                  Добавить производство
                </Button>
              </Col>
              <Col>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={refresh}
                  loading={loading}
                  size="large"
                >
                  Обновить
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Таблица */}
          <Card>
            <Table
              columns={columns}
              dataSource={logs}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1200 }}
              pagination={{
                current: Math.floor(pagination.offset / pagination.limit) + 1,
                pageSize: pagination.limit,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
                pageSizeOptions: ['20', '50', '100'],
              }}
              onChange={handleTableChange}
            />
          </Card>
        </>
      )}

      {/* Статистика */}
      {activeTab === 'stats' && (
        <div>
          <div className="mb-4">
            <Space>
              <Button onClick={() => loadStats(7)}>7 дней</Button>
              <Button onClick={() => loadStats(14)}>14 дней</Button>
              <Button onClick={() => loadStats(30)} type="primary">30 дней</Button>
              <Button onClick={() => loadStats(90)}>90 дней</Button>
            </Space>
          </div>
          
          <ManufacturingStats stats={stats} loading={loading} />
        </div>
      )}

      {/* Форма добавления */}
      <ManufacturingForm
        visible={formVisible}
        onClose={() => setFormVisible(false)}
        onSuccess={refresh}
      />
    </div>
  );
};